import { useMemo } from "react";
import * as moment from "moment";
import {
    ColDef,
    FilterChangedEvent,
    FirstDataRenderedEvent,
    GridReadyEvent,
    ICellRendererParams,
    IHeaderParams,
    RowClassParams,
    SortChangedEvent,
    ValueFormatterParams,
    ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { IHardToMatchPair } from "@nkr/models/hard-to-match/hard-to-match-pair";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import OverlayMenuButton, { IMenuItem } from "@nkr/components/overlay-menu-button";
import { HardToMatchGridWrapper, hiddenRowStyle } from "./styles";

interface IProps {
    rowData: IHardToMatchPair[];
    onGridReady?: (e: GridReadyEvent) => void;
    onEdit: (pair: IHardToMatchPair) => void;
}

const yesNo = (value: boolean | null): string => (value == null ? "" : value ? "Yes" : "No");

const formatSurgeryDate = (p: ValueFormatterParams): string =>
    p.value ? moment(p.value).format("MM/DD/YYYY") : "--";

const formatBlankAsDashes = (p: ValueFormatterParams): string => p.value || "--";

const Grid = ({ rowData, onGridReady, onEdit }: IProps) => {
    const refreshRowNumbers = (e: SortChangedEvent | FilterChangedEvent) => {
        e.api.refreshCells({ columns: ["rowNumber"], force: true });
    };

    const actionMenu = (pair: IHardToMatchPair): IMenuItem[] => [
        {
            id: 0,
            action: 0,
            include: true,
            selectable: false,
            element: (
                <a
                    target="_blank"
                    href={`/center/${pair.recipientCenterId}/recipient/${pair.recipientId}/evaluate-donor/${pair.donorId}`}
                >
                    Evaluate Donor
                </a>
            ),
        },
        {
            id: 1,
            action: 0,
            include: true,
            selectable: false,
            element: (
                <a role="button" onClick={() => onEdit(pair)}>
                    Update Status
                </a>
            ),
        },
    ];

    const defaultColDef: ColDef = {
        sortable: true,
        resizable: true,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        headerComponentFramework: (p: IHeaderParams) => (
            <SortableHeaderComponent {...p} menuIcon="filter" menuIconLocation="RIGHT">
                {p.displayName}
            </SortableHeaderComponent>
        ),
    };

    const cols: ColDef[] = [
        {
            colId: "rowNumber",
            headerName: "R#",
            headerClass: "review-header",
            minWidth: 60,
            sortable: false,
            filter: false,
            suppressMenu: true,
            valueGetter: (p: ValueGetterParams) => p.node.rowIndex + 1,
        },
        {
            field: "recipientAlias",
            headerName: "Recipient",
            headerClass: "recipient-header",
            minWidth: 110,
            cellRendererFramework: ({ data, value }: ICellRendererParams) => (
                <a
                    href={`/center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/general`}
                    target="_blank"
                >
                    {value}
                </a>
            ),
        },
        { field: "recipientCenterAlias", headerName: "R Center", headerClass: "recipient-header", minWidth: 110 },
        { field: "recipientPatientTypeValue", headerName: "R Type", headerClass: "recipient-header", minWidth: 90 },
        { field: "recipientBloodType", headerName: "R ABO", headerClass: "recipient-header", minWidth: 80 },
        { field: "recipientAge", headerName: "R Age", headerClass: "recipient-header", minWidth: 80 },
        { field: "recipientCpra", headerName: "cPRA", headerClass: "recipient-header", minWidth: 80 },
        { field: "recipientEstimatedWaitTime", headerName: "EWT", headerClass: "recipient-header", minWidth: 80 },
        { field: "recipientActiveWaitMonths", headerName: "AWT", headerClass: "recipient-header", minWidth: 80 },
        {
            colId: "recipientIsOnDialysis",
            headerName: "Dialysis",
            headerClass: "recipient-header",
            minWidth: 90,
            valueGetter: ({ data }: ValueGetterParams) => yesNo(data.recipientIsOnDialysis),
        },
        {
            field: "donorAlias",
            headerName: "Donor",
            headerClass: "donor-header",
            minWidth: 110,
            cellRendererFramework: ({ data, value }: ICellRendererParams) => (
                <a
                    href={`/center/${data.donorCenterId}/donor/${data.donorId}/profile/donor-data`}
                    target="_blank"
                >
                    {value}
                </a>
            ),
        },
        { field: "donorCenterAlias", headerName: "D Center", headerClass: "donor-header", minWidth: 110 },
        {
            colId: "isChainStart",
            headerName: "Chain Start",
            headerClass: "donor-header",
            minWidth: 100,
            valueGetter: ({ data }: ValueGetterParams) => yesNo(data.isChainStart),
        },
        {
            colId: "surgeryDate",
            headerName: "Surgery Date",
            headerClass: "donor-header",
            minWidth: 120,
            valueGetter: ({ data }: ValueGetterParams) =>
                data.surgeryDate ? moment(data.surgeryDate).format("YYYY-MM-DD") : null,
            valueFormatter: formatSurgeryDate,
            filterParams: { valueFormatter: formatSurgeryDate },
        },
        { field: "donorBloodType", headerName: "D ABO", headerClass: "donor-header", minWidth: 80 },
        { field: "donorAge", headerName: "D Age", headerClass: "donor-header", minWidth: 80 },
        { field: "donorLaterality", headerName: "Lat", headerClass: "donor-header", minWidth: 70 },
        { field: "donorCreatinineClearance", headerName: "CCl", headerClass: "donor-header", minWidth: 70 },
        { field: "epletMmRisk", headerName: "EMM", headerClass: "match-header", minWidth: 80 },
        { field: "hlaMatchPoints", headerName: "HLA Pts", headerClass: "match-header", minWidth: 90 },
        { field: "avoidsCount", headerName: "# Avoids", headerClass: "match-header", minWidth: 90, sort: "asc" },
        {
            field: "avoidsList",
            headerName: "Avoids",
            headerClass: "match-header",
            minWidth: 110,
            valueFormatter: formatBlankAsDashes,
        },
        {
            field: "reviewStatusDescription",
            headerName: "Status",
            headerClass: "review-header",
            minWidth: 170,
            valueFormatter: formatBlankAsDashes,
        },
        {
            field: "reviewNote",
            headerName: "Notes",
            headerClass: "review-header",
            minWidth: 180,
            valueFormatter: formatBlankAsDashes,
            filter: "agTextColumnFilter",
            tooltipField: "reviewNote",
        },
        {
            colId: "action",
            cellClass: "action-menu",
            headerName: "",
            headerClass: "review-header",
            minWidth: 90,
            sortable: false,
            filter: false,
            suppressMenu: true,
            cellRendererFramework: ({ data }: ICellRendererParams) => (
                <OverlayMenuButton
                    menu={actionMenu(data)}
                    bsStyle="primary"
                    bsSize="xs"
                    title="Actions"
                    listItemStyle={{ padding: "2px 7.5px", fontSize: 14 }}
                    includeCaret={true}
                    onSelect={() => {}}
                />
            ),
        },
    ];

    const columnDefs = useMemo(() => cols, []);

    return (
        <HardToMatchGridWrapper className="ag-theme-bootstrap" height="calc(100vh - 230px)">
            <AgGridReact
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowData={rowData}
                rowHeight={30}
                getRowStyle={(p: RowClassParams) => (p.data.isHidden ? hiddenRowStyle : null)}
                headerHeight={40}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                noRowsOverlayComponentFramework={() => <div>No hard-to-match pairs found.</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onSortChanged={refreshRowNumbers}
                onFilterChanged={refreshRowNumbers}
            />
        </HardToMatchGridWrapper>
    );
};

export default Grid;
